import {
  Button,
  ButtonGroup,
  OutlineCircleQuestionIcon,
  Heading,
  Tooltip,
} from '@frontend/legacy-components';

import {
  ViewerPopoverButton,
  ViewerToolsBar,
} from 'modules/common/viewer/components';

import { isMobileBrowser } from 'utils/browserUtils';

import { useDxfViewerSession } from '../DxfViewerContext';
import { useReadableStore } from '../session/useSessionState';
import { DxfBrushToolGroup } from './DxfBrushToolGroup';
import { DxfViewerControlsInfoRows } from './DxfViewerControlsInfoRows';

const NO_CHANGES_TO_SAVE = 'No changes to save';

export type DxfMappingToolbarProps = {
  /** Leaves mapping mode, confirming first when there are edits to discard. */
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
};

/**
 * The second bar, shown in place of the viewing tools while mapping.
 *
 * Measure, axis dimensions and the bounding box are deliberately absent — the spec
 * scopes them to viewing mode, and `modeController` has already switched them off,
 * so there is nothing here to turn them back on with.
 */
export const DxfMappingToolbar = ({
  onCancel,
  onSave,
  isSaving = false,
}: DxfMappingToolbarProps) => {
  const session = useDxfViewerSession();
  const mapping = useReadableStore(session.mapping);
  const isDirty = useReadableStore(session.hasUnsavedMapping);
  const tool = useReadableStore(session.tools);
  const isMobile = isMobileBrowser();
  const isLocked = mapping.lockReason !== undefined;
  // First create seeds draft === baseline (default cut roles), so the draft
  // matches the entry baseline until the user edits — but that seed is still
  // worth persisting, and Save must not wait for a no-op change. Edits to an
  // existing mapping still require roles that differ from the saved baseline.
  const canSave = !isLocked && (isDirty || mapping.saved === null);
  const saveDisabledReason = canSave
    ? undefined
    : mapping.lockReason ?? NO_CHANGES_TO_SAVE;

  return (
    <ViewerToolsBar px={0.75}>
      <Heading size="md">Tools</Heading>

      {/*
        The tool radio group: Choose (plain selection) plus one brush per role.
        A brush arms on click and paints on a later click/marquee, which is a
        different action from the flyout's "assign straight to the selection" —
        see `DxfBrushToolGroup` for why they are not the same component.
      */}
      <DxfBrushToolGroup
        activeTool={tool}
        onArmBrush={session.tools.armBrush}
        onChoose={() => session.tools.set('select')}
        isDisabled={isLocked}
        disabledReason={mapping.lockReason}
      />

      {/* The only place the undo/redo chords are documented, and the only toolbar
          they work from. */}
      <ViewerPopoverButton
        title="Mapping controls"
        icon={<OutlineCircleQuestionIcon boxSize={3} />}
      >
        <DxfViewerControlsInfoRows isMobile={isMobile} isMapping />
      </ViewerPopoverButton>

      <ButtonGroup spacing={1}>
        <Button size="md" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        {/*
          A file locked mid-session cannot be saved — the spec is explicit that the
          changes fail rather than being silently dropped. Saying why beats a dead
          button the user has no way to interpret.
        */}
        <Tooltip
          placement="bottom"
          label={saveDisabledReason}
          isDisabled={saveDisabledReason === undefined}
        >
          <Button
            size="md"
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={!canSave}
          >
            Save Mapping
          </Button>
        </Tooltip>
      </ButtonGroup>
    </ViewerToolsBar>
  );
};
DxfMappingToolbar.displayName = 'DxfMappingToolbar';
